import { useState } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Section";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2, Building2 } from "lucide-react";

export default function Contact() {
  const { t, content } = useApp();
  const info = content.contact_info || {};
  const c = t.contactX;
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "", message: "", vertical: "general" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(c.success);
      setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "", vertical: "general" });
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const cards = [
    { icon: MapPin, title: c.hq, value: info.address },
    { icon: Phone, title: c.hotline, value: info.phone, href: `tel:${info.phone}` },
    { icon: Mail, title: c.email, value: info.email, href: `mailto:${info.email}` },
    { icon: Building2, title: "CIN", value: info.cin },
  ];

  return (
    <div>
      <Section sid="contact.head" name="Contact Header" className="border-b border-teal-900/40 bg-[#E9F5F4]/40 py-14">
        <div className="oj-container">
          <div className="oj-eyebrow mb-3"><Building2 className="w-4 h-4" /> {c.secretariat}</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0B3B39] tracking-tight">{t.contactPage.title}</h1>
          <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">{c.intro}</p>
        </div>
      </Section>

      <Section sid="contact.body" name="Contact Details & Form" className="oj-container py-12 grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {cards.map((card, i) => {
            const Icon = card.icon;
            const inner = (
              <div className="oj-card p-6 flex gap-4 items-start h-full">
                <div className="grid place-items-center w-11 h-11 rounded-xl bg-[#017471] border border-teal-600/40 shrink-0"><Icon className="w-5 h-5 text-white" /></div>
                <div><div className="text-xs font-mono uppercase tracking-wider text-slate-500">{card.title}</div><div className="mt-1 text-slate-700 text-sm leading-relaxed">{card.value}</div></div>
              </div>
            );
            return <Reveal key={card.title} delay={i * 0.07}>{card.href ? <a href={card.href} className="block hover:opacity-90 transition-opacity">{inner}</a> : inner}</Reveal>;
          })}
        </div>

        <Reveal delay={0.15} className="lg:col-span-3">
          <form onSubmit={submit} className="oj-card p-8" data-testid="contact-form">
            <h3 className="oj-h3 mb-6">{c.formTitle}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input required placeholder={c.ph.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="oj-input" data-testid="contact-name" />
              <input required type="email" placeholder={c.ph.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="oj-input" data-testid="contact-email" />
              <input placeholder={c.ph.phone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="oj-input" data-testid="contact-phone" />
              <input placeholder={c.ph.company} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="oj-input" data-testid="contact-company" />
              <select value={form.vertical} onChange={(e) => setForm({ ...form, vertical: e.target.value })} className="oj-input sm:col-span-2" data-testid="contact-vertical">
                {c.verticals.map(([v, l]) => <option key={v} value={v} className="bg-teal-950">{l}</option>)}
              </select>
              <input required placeholder={c.ph.subject} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="oj-input sm:col-span-2" data-testid="contact-subject" />
              <textarea required rows={5} placeholder={c.ph.message} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="oj-input resize-none sm:col-span-2" data-testid="contact-message" />
            </div>
            <button disabled={loading} className="oj-btn-gold w-full mt-5" data-testid="contact-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t.contactPage.send}
            </button>
          </form>
        </Reveal>
      </Section>
    </div>
  );
}
